import { ScanButton } from "@/components/ScanButton";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useFocusRow } from "@/lib/useFocusRow";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DriverPicker } from "@/components/drivers/DriverPicker";
import { Plus, Trash2, FileText, Upload, Settings2, Pencil, Building2, ChevronDown, ChevronRight } from "lucide-react";

function expiryBadge(dateStr, noExpiration, reviewDue) {
  if (noExpiration) {
    if (!reviewDue) return <Badge className="bg-slate-100 text-slate-700 border border-slate-200" data-testid="cert-no-expiration-badge">No expiration</Badge>;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Math.round((new Date(reviewDue) - today) / 86400000);
    const tone = days < 0 ? "bg-rose-50 text-rose-700 border-rose-200" : days <= 30 ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-100 text-slate-700 border-slate-200";
    return <Badge className={`${tone} border`} data-testid="cert-review-badge">{days < 0 ? `Review overdue · ${reviewDue}` : `Review due ${reviewDue}`}</Badge>;
  }
  if (!dateStr) return <span className="text-slate-400">—</span>;
  const d = new Date(dateStr);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Expired · {dateStr}</Badge>;
  if (days <= 30) return <Badge className="bg-amber-50 text-amber-800 border border-amber-300">{days}d · {dateStr}</Badge>;
  return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">{dateStr}</Badge>;
}

export default function Certificates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [certs, setCerts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [terminals, setTerminals] = useState([]);
  useEffect(() => { api.get("/terminals").then(({ data }) => setTerminals(Array.isArray(data) ? data : [])).catch(() => setTerminals([])); }, []);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [manageTypes, setManageTypes] = useState(false);
  const emptyForm = { cert_type: "", driver_id: "", customer_id: "", issued_date: "", expiry_date: "", no_expiration: false, review_yearly: false, notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [params, setParams] = useSearchParams();
  const customerFilter = params.get("customer") || "all";
  useFocusRow("cert-row", certs.length > 0);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const uploadFile = async (certId, f) => {
    const fd = new FormData();
    fd.append("file", f);
    await api.post(`/certificates/${certId}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const openFile = async (c) => {
    try {
      const res = await api.get(`/certificates/${c.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const attachFile = async (c, f) => {
    if (!f) return;
    try { await uploadFile(c.id, f); toast.success("File uploaded"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: d }, { data: t }] = await Promise.all([api.get("/certificates"), api.get("/drivers"), api.get("/certificate-types")]);
      setCerts(c); setDrivers(d); setTypes(t);
      if (isAdmin) { const { data: cu } = await api.get("/customers"); setCustomers(cu); }
      if (t.length) setForm((f) => (f.cert_type ? f : { ...f, cert_type: t[0].name }));
    } catch (err) { toast.error(formatApiError(err)); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setEditing(null); setFile(null); setForm({ ...emptyForm, cert_type: types[0]?.name || "", no_expiration: !!types[0]?.default_no_expiration, customer_id: isAdmin && customerFilter !== "all" ? customerFilter : "" }); setOpen(true); };
  const formDrivers = isAdmin ? drivers.filter((d) => !form.customer_id || d.customer_id === form.customer_id) : drivers;

  const onTypeChange = (v) => {
    const t = types.find((x) => x.name === v);
    const upd = { ...form, cert_type: v };
    if (t?.default_no_expiration) { upd.no_expiration = true; upd.expiry_date = ""; }
    else if (t?.default_validity_months && form.issued_date && !form.expiry_date) {
      const d = new Date(form.issued_date); d.setMonth(d.getMonth() + t.default_validity_months);
      upd.expiry_date = d.toISOString().slice(0, 10);
    }
    setForm(upd);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.driver_id) payload.driver_id = null;
      if (isAdmin) payload.customer_id = payload.customer_id || null; else delete payload.customer_id;
      if (!payload.issued_date) payload.issued_date = null;
      if (payload.no_expiration) payload.expiry_date = null; else payload.review_yearly = false;
      setSaving(true);
      if (editing) {
        await api.patch(`/certificates/${editing.id}`, payload);
        if (file) await uploadFile(editing.id, file);
        toast.success("Certificate updated");
      } else {
        const { data } = await api.post("/certificates", payload);
        if (file) await uploadFile(data.id, file);
        toast.success(file ? "Certificate and file saved" : "Certificate added");
      }
      setOpen(false); setEditing(null); setFile(null); setForm({ ...emptyForm, cert_type: types[0]?.name || "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({ cert_type: c.cert_type, driver_id: c.driver_id || "", customer_id: c.owner_customer_id || c.customer_id || "", issued_date: c.issued_date || "", expiry_date: c.expiry_date || "", no_expiration: !!c.no_expiration, review_yearly: !!c.review_yearly, notes: c.notes || "" });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this certificate?")) return;
    try { await api.delete(`/certificates/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const driverName = (id) => {
    const d = drivers.find((x) => x.id === id);
    return d ? `${d.first_name} ${d.last_name}` : "—";
  };
  const visible = customerFilter === "all" ? certs : certs.filter((c) => c.owner_customer_id === customerFilter);
  const grouped = isAdmin && customerFilter === "all";
  const PALETTE = [
    ["border-l-blue-500", "bg-blue-50", "text-blue-800"], ["border-l-emerald-500", "bg-emerald-50", "text-emerald-800"], ["border-l-violet-500", "bg-violet-50", "text-violet-800"],
    ["border-l-amber-500", "bg-amber-50", "text-amber-800"], ["border-l-rose-500", "bg-rose-50", "text-rose-800"], ["border-l-teal-500", "bg-teal-50", "text-teal-800"],
    ["border-l-fuchsia-500", "bg-fuchsia-50", "text-fuchsia-800"], ["border-l-sky-500", "bg-sky-50", "text-sky-800"], ["border-l-lime-500", "bg-lime-50", "text-lime-800"], ["border-l-orange-500", "bg-orange-50", "text-orange-800"],
  ];
  const groups = grouped ? [...visible.reduce((m, c) => { const k = c.owner_customer_id || "internal"; if (!m.has(k)) m.set(k, { id: k, name: c.company || "Safer Highways Inc", certs: [] }); m.get(k).certs.push(c); return m; }, new Map()).values()]
    .sort((a, b) => (a.id === "internal") - (b.id === "internal") || a.name.trim().localeCompare(b.name.trim(), undefined, { sensitivity: "base", numeric: true })).map((g, i) => ({ ...g, tone: PALETTE[i % PALETTE.length] })) : [];
  const [expanded, setExpanded] = useState({});
  const focusId = params.get("focus");
  useEffect(() => {
    if (!focusId || !grouped) return;
    const c = certs.find((x) => x.id === focusId);
    if (c) setExpanded((s) => ({ ...s, [c.owner_customer_id || "internal"]: true }));
  }, [focusId, certs, grouped]);
  const collapsed = Object.fromEntries(groups.map((g) => [g.id, !expanded[g.id]]));
  const rowsToRender = grouped ? groups.flatMap((g) => [{ group: g }, ...(collapsed[g.id] ? [] : g.certs)]) : visible;
  const allCollapsed = grouped && groups.length > 0 && groups.every((g) => collapsed[g.id]);
  const isExpiring = (c) => !c.no_expiration && c.expiry_date && (new Date(c.expiry_date) - Date.now()) / 86400000 <= 30;
  const markReviewed = async (c) => {
    try { await api.post(`/certificates/${c.id}/reviewed`); toast.success(`${c.cert_type} marked as reviewed today`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="certificates-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="tag-mono text-blue-700 mb-1">COMPLIANCE</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900">Certificates</h1>
          <p className="text-slate-600 mt-1">Track document expirations and compliance certificates.</p>
          {isAdmin && (
            <div className="mt-3">
              <Select value={customerFilter} onValueChange={(v) => setParams(v === "all" ? {} : { customer: v })}>
                <SelectTrigger className="w-full sm:w-72" data-testid="certs-customer-filter"><SelectValue placeholder="All customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers ({certs.length})</SelectItem>
                  {customers.map((cu) => <SelectItem key={cu.id} value={cu.id}>{cu.company || cu.name} ({certs.filter((c) => c.owner_customer_id === cu.id).length})</SelectItem>)}
                </SelectContent>
              </Select>
              {grouped && groups.length > 1 && <Button variant="ghost" size="sm" className="ml-2 text-slate-600" onClick={() => setExpanded(allCollapsed ? Object.fromEntries(groups.map((g) => [g.id, true])) : {})} data-testid="cert-toggle-all-groups">{allCollapsed ? "Expand all" : "Collapse all"}</Button>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <Button variant="outline" onClick={() => setManageTypes(true)} data-testid="manage-cert-types-button"><Settings2 className="h-4 w-4 mr-2" />Customize types</Button>}
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setFile(null); setForm({ ...emptyForm, cert_type: types[0]?.name || "" }); } }}>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="add-certificate-button"><Plus className="h-4 w-4 mr-2" />Add Certificate</Button>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit Certificate" : "Add Certificate"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              {isAdmin && (
                <div>
                  <Label>Company</Label>
                  <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v, driver_id: "" })}>
                    <SelectTrigger data-testid="cert-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Safer Highways Inc (internal) —</SelectItem>
                      {customers.map((cu) => (<SelectItem key={cu.id} value={cu.id}>{cu.company || cu.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Type</Label>
                <Select value={form.cert_type} onValueChange={onTypeChange}>
                  <SelectTrigger data-testid="cert-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (<SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>))}
                    {editing && !types.some((t) => t.name === form.cert_type) && <SelectItem value={form.cert_type}>{form.cert_type}</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Driver (optional)</Label>
                <DriverPicker drivers={formDrivers} terminals={terminals} value={form.driver_id || ""} onChange={(v) => setForm({ ...form, driver_id: v })} allowManual={false} noneLabel="— Company-level (no driver) —" groupBy="none" testid="cert-driver-picker" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Issued</Label><Input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} /></div>
                <div>
                  <div className="flex items-center justify-between"><Label>Expiry</Label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"><Switch checked={form.no_expiration} onCheckedChange={(v) => setForm({ ...form, no_expiration: v, expiry_date: v ? "" : form.expiry_date })} className="scale-75" data-testid="cert-no-expiration-toggle" />No expiration</label>
                  </div>
                  <Input required={!form.no_expiration} disabled={form.no_expiration} type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="cert-expiry-input" />
                  {form.no_expiration && (
                    <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><Switch checked={form.review_yearly} onCheckedChange={(v) => setForm({ ...form, review_yearly: v })} className="scale-75" data-testid="cert-review-yearly-toggle" />Remind me to review yearly</label>
                  )}
                </div>
              </div>
              <div>
                <Label>{editing?.file_path ? "Replace file (PDF / image)" : "Certificate file (PDF / image)"}</Label>
                <Input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cert-file-input" />
                <ScanButton onFile={setFile} testid="cert-file-scan" className="mt-1" />
                {file && <div className="text-xs text-slate-500 mt-1 font-mono">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <DialogFooter><Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="cert-save-button">{saving ? "Saving…" : editing ? "Save changes" : "Save"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <CertTypesDialog open={manageTypes} onClose={() => { setManageTypes(false); load(); }} />

      <Card>
        <Table data-testid="certificates-table" stickyHeader>
          <TableHeader sticky data-testid="certificates-table-header"><TableRow>{isAdmin && !grouped && <TableHead>Company</TableHead>}<TableHead>Type</TableHead><TableHead>Driver</TableHead><TableHead>Issued</TableHead><TableHead>Expiry</TableHead><TableHead>File</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {visible.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-500">No certificates{customerFilter !== "all" ? " for this customer" : ""}.</TableCell></TableRow>}
            {rowsToRender.map((c) => c.group ? (
              <TableRow key={`g-${c.group.id}`} className={`${c.group.tone[1]} hover:${c.group.tone[1]} border-l-4 ${c.group.tone[0]} cursor-pointer select-none`} onClick={() => setExpanded((s) => ({ ...s, [c.group.id]: !s[c.group.id] }))} data-testid={`cert-group-${c.group.id}`}>
                <TableCell colSpan={6} className="py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className={`flex items-center gap-2 font-semibold ${c.group.tone[2]}`}>{collapsed[c.group.id] ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}<Building2 className="h-4 w-4" />{c.group.name}<span className="text-xs font-mono opacity-70">({c.group.certs.length})</span>
                      {c.group.certs.filter(isExpiring).length > 0 && <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-white/80 border border-current" data-testid={`cert-group-expiring-${c.group.id}`}>{c.group.certs.filter(isExpiring).length} expiring / expired</span>}</div>
                    {c.group.id !== "internal" && <Link to={`/customers/${c.group.id}`} onClick={(e) => e.stopPropagation()} className={`text-xs ${c.group.tone[2]} hover:underline`}>View customer →</Link>}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={c.id} data-testid={`cert-row-${c.id}`}>
                {isAdmin && !grouped && <TableCell className="text-sm text-blue-700" data-testid={`cert-company-${c.id}`}>{c.company}</TableCell>}
                <TableCell className="font-medium">{c.cert_type}</TableCell>
                <TableCell>{c.driver_id ? driverName(c.driver_id) : <span className="text-slate-400">Company-level</span>}</TableCell>
                <TableCell className="font-mono text-xs">{c.issued_date || "—"}</TableCell>
                <TableCell>
                  {expiryBadge(c.expiry_date, c.no_expiration, c.review_due)}
                  {c.review_due && <button type="button" className="ml-2 text-xs text-blue-700 hover:underline" onClick={() => markReviewed(c)} title={c.last_reviewed_at ? `Last reviewed ${c.last_reviewed_at} by ${c.last_reviewed_by || "—"}` : "Not reviewed yet"} data-testid={`cert-mark-reviewed-${c.id}`}>Mark reviewed</button>}
                </TableCell>
                <TableCell>
                  {c.file_path ? (
                    <Button variant="link" className="p-0 h-auto text-blue-700" onClick={() => openFile(c)} data-testid={`cert-download-${c.id}`}><FileText className="h-4 w-4 mr-1" />{c.file_name || "Download"}</Button>
                  ) : (
                    <label className="text-sm text-slate-500 hover:text-blue-700 cursor-pointer inline-flex items-center gap-1" data-testid={`cert-upload-${c.id}`}>
                      <Upload className="h-4 w-4" /> Upload
                      <input type="file" className="hidden" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(e) => attachFile(c, e.target.files?.[0])} />
                    </label>
                  )}
                  {!c.file_path && <ScanButton onFile={(f) => attachFile(c, f)} label="Scan" testid={`cert-scan-${c.id}`} className="ml-2 text-sm text-slate-500 hover:text-blue-700" />}
                  {c.ai_status === "pending" && <span className="ml-2 text-[11px] text-violet-700" data-testid={`cert-ai-${c.id}`}>AI reading…</span>}
                  {c.ai_status === "done" && <span className="ml-2 text-[11px] text-violet-700" data-testid={`cert-ai-${c.id}`}>AI read{c.ai_suggestions?.length ? ` · ${c.ai_suggestions.length} violation(s) drafted` : ""}{c.ai_license?.auto_applied ? " · license updated" : ""}</span>}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(c)} data-testid={`edit-cert-${c.id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(c.id)} data-testid={`delete-cert-${c.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function CertTypesDialog({ open, onClose }) {
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");
  const [months, setMonths] = useState("");
  const [noExp, setNoExp] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/certificate-types"); setTypes(data); }
    catch (err) { toast.error(formatApiError(err)); }
  }, []);
  const reset = () => { setName(""); setMonths(""); setNoExp(false); setEditId(null); };
  useEffect(() => { if (open) { load(); reset(); } }, [open, load]);

  const save = async (e) => {
    e.preventDefault();
    const body = { name, default_validity_months: months && !noExp ? Number(months) : null, default_no_expiration: noExp };
    try {
      if (editId) { await api.patch(`/certificate-types/${editId}`, body); toast.success("Type updated"); }
      else { await api.post("/certificate-types", body); toast.success("Type added"); }
      reset(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.name}" from the list? Existing certificates keep their label.`)) return;
    try { await api.delete(`/certificate-types/${t.id}`); toast.success("Type removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cert-types-dialog">
        <DialogHeader><DialogTitle>Customize certificate types</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-[1fr_120px_auto] gap-2 items-end">
          <div><Label>{editId ? "Rename type" : "New type"}</Label><Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Clearinghouse Query Consent" data-testid="cert-type-name-input" /></div>
          <div><Label>Valid (months)</Label><Input type="number" min="1" value={months} disabled={noExp} onChange={(e) => setMonths(e.target.value)} placeholder="opt." data-testid="cert-type-months-input" /></div>
          <Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="cert-type-save-button">{editId ? "Update" : "Add"}</Button>
        </form>
        <div className="flex items-center justify-between -mt-1">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><Switch checked={noExp} onCheckedChange={(v) => { setNoExp(v); if (v) setMonths(""); }} className="scale-75" data-testid="cert-type-no-expiration-toggle" />Defaults to "No expiration"</label>
          {editId && <button type="button" className="text-xs text-slate-500 underline" onClick={reset}>Cancel edit</button>}
        </div>
        <div className="divide-y divide-slate-200 border border-slate-200 rounded-md max-h-72 overflow-auto">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`cert-type-row-${t.id}`}>
              <div><div className="font-medium">{t.name}</div>{t.default_no_expiration ? <div className="text-xs text-slate-500" data-testid={`cert-type-noexp-${t.id}`}>No expiration by default</div> : t.default_validity_months ? <div className="text-xs text-slate-500">Default validity: {t.default_validity_months} months</div> : null}</div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => { setEditId(t.id); setName(t.name); setMonths(t.default_validity_months || ""); setNoExp(!!t.default_no_expiration); }} data-testid={`cert-type-edit-${t.id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`cert-type-delete-${t.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
